import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, take, takeUntil, tap } from 'rxjs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ButtonComponent } from '@examdojo/core/button';
import { DraggablePaneComponent, DraggablePaneOptions } from '@examdojo/core/draggable-pane';
import { IconComponent } from '@examdojo/core/icon';
import { JsonEditorComponent, provideJsonEditor } from '@examdojo/core/json-editor';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import {
  WidgetComponent as CoreWidgetComponent,
  WidgetComponentConfig,
  WidgetOrchestratorService,
} from '@examdojo/core/widget';
import { provideJsxgraphWidgets } from '@examdojo/math-widget';
import { GraphpadEditorComponent, type GraphpadWidgetConfig } from '@examdojo/math-widget/widgets/graphpad';
import { v4 as uuid } from 'uuid';
import { WidgetBlock } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { DEFAULT_WIDGET_PROPS } from './widget.model';

const MIN_HEIGHT = 200;
const MAX_HEIGHT = 800;
const DEFAULT_HEIGHT = 400;

@Component({
  selector: 'dojo-widget-node',
  standalone: true,
  imports: [
    ButtonComponent,
    CoreWidgetComponent,
    DraggablePaneComponent,
    GraphpadEditorComponent,
    JsonEditorComponent,
    MatButtonToggleModule,
    ReactiveFormsModule,
    IconComponent,
  ],
  templateUrl: './widget.component.html',
  styleUrl: './widget.component.scss',
  hostDirectives: [DragWithHandleOnlyDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideJsxgraphWidgets(),
    provideJsonEditor({}),
    WidgetOrchestratorService,
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => WidgetNodeComponent),
    },
  ],
})
export class WidgetNodeComponent extends NodeViewComponent<WidgetBlock> {
  constructor() {
    super();

    // Sync config signal -> FormControl
    effect(() => {
      const config = this.config();
      const json = JSON.stringify(config, null, 2);
      if (untracked(() => this.configControl.value) !== json) {
        this.configControl.setValue(json, { emitEvent: false });
      }
    });

    // Sync FormControl -> node config
    this.configControl.valueChanges
      .pipe(
        tap((value) => this.onConfigChange(value)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly config = this.getProp('config');
  readonly height = this.getProp('height');

  readonly containerHeight = computed(() => this.height() ?? DEFAULT_HEIGHT);

  readonly widgetConfig = computed<WidgetComponentConfig>(() => {
    const config = this.config() as unknown as WidgetComponentConfig | undefined;
    return {
      ...config,
      // Ensure a unique ID for each widget instance
      id: config?.['id'] ?? `widget-${uuid()}`,
    } as WidgetComponentConfig;
  });

  readonly graphpadConfig = computed<GraphpadWidgetConfig | null>(() => {
    const config = this.widgetConfig();
    return config.type === 'graphpad' ? (config as unknown as GraphpadWidgetConfig) : null;
  });

  readonly showEditor = signal(false);
  readonly editorMode = signal<'json' | 'visual'>('json');
  readonly isGraphpad = computed(() => this.graphpadConfig() !== null);
  readonly configControl = new FormControl<string>('', { nonNullable: true });
  readonly configErrors = signal<string[] | null>(null);
  readonly editorPaneOptions = signal<DraggablePaneOptions>({
    position: { x: 16, y: 80 },
    size: { width: 460, height: 700 },
    minWidth: 320,
    minHeight: 280,
  });

  private readonly elementRef = inject(ElementRef);
  private resizeStartY = 0;
  private resizeStartHeight = 0;

  override getDefaults = (): WidgetBlock => ({
    ...DEFAULT_WIDGET_PROPS,
    id: uuid(),
  });

  toggleEditor(): void {
    this.showEditor.update((v) => !v);
    if (this.showEditor()) {
      const json = JSON.stringify(this.config(), null, 2);
      this.configControl.setValue(json, { emitEvent: false });
      this.configErrors.set(null);

      // Position the pane next to the widget without overlapping it
      const rect = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();
      const paneWidth = 460;
      const paneHeight = 700;
      const gap = 12;
      const margin = 16;

      // Try right side first, fall back to left
      const spaceRight = window.innerWidth - rect.right - gap - margin;
      const spaceLeft = rect.left - gap - margin;
      let x: number;
      if (spaceRight >= paneWidth) {
        x = rect.right + gap;
      } else if (spaceLeft >= paneWidth) {
        x = rect.left - gap - paneWidth;
      } else {
        // Not enough room on either side — position at right edge of viewport
        x = window.innerWidth - paneWidth - margin;
      }

      // Align Y to viewport top area, ensuring the pane fits vertically
      const y = Math.max(margin, Math.min(rect.top, window.innerHeight - paneHeight - margin));

      this.editorPaneOptions.set({
        ...this.editorPaneOptions(),
        position: { x, y },
        size: { width: paneWidth, height: paneHeight },
      });
    }
  }

  onVisualEditorChange(config: GraphpadWidgetConfig): void {
    const json = JSON.stringify(config, null, 2);
    this.configControl.setValue(json, { emitEvent: false });
    this.configErrors.set(null);
    this.updateProp('config', config as unknown as Record<string, unknown>);
  }

  onConfigChange(value: string): void {
    try {
      const parsed = JSON.parse(value);
      this.configErrors.set(null);
      this.updateProp('config', parsed);
    } catch (e) {
      this.configErrors.set([(e as Error).message]);
    }
  }

  onHeightResizeStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStartY = event.clientY;
    this.resizeStartHeight = this.containerHeight();

    const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup').pipe(take(1));

    fromEvent<MouseEvent>(document, 'mousemove')
      .pipe(
        tap((moveEvent) => {
          const deltaY = moveEvent.clientY - this.resizeStartY;
          const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, this.resizeStartHeight + deltaY));
          this.updateProp('height', newHeight);
        }),
        takeUntil(mouseUp$),
      )
      .subscribe();
  }
}
